import { Plus, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { CurrencySelect } from '@components/currency-select';
import { Label } from '@components/label';
import { TextField } from '@components/textfield';
import { Button } from '@components/ui/button';
import { injectTSDI } from '@core/tsdi';
import { ChallengeDataStore } from '../../data-store';
import {
    formatChallengeResult,
    storedValueDiffersFromDisplay
} from '../../../leaderboard/raw-value';
import { challengeTargetUnit } from '../../../rule-types';
import { buildChallengeRulePayload } from '../rule-payload';
import { RuleSetSection } from '../rule-set-section';
import { ChallengeRulesStore } from '../store';
import { GroupRuleFormType, GroupRulesStore } from './store';

const GroupRules = observer(() => {
    const { disableGroupRulesEdit } = injectTSDI(ChallengeDataStore);
    const {
        fields: { groupRules, ruleType, fitnessMetricType },
        form,
        validateRules
    } = injectTSDI(ChallengeRulesStore);
    const groupRulesStore = injectTSDI(GroupRulesStore);
    const { groupRulesList, addGroupRule, removeGroupRule } = groupRulesStore;

    const resultFormat = buildChallengeRulePayload(form);

    const goalHint = (goal: number) => {
        const unit = challengeTargetUnit(
            ruleType.value,
            fitnessMetricType.value
        );
        if (!(goal > 0) || !storedValueDiffersFromDisplay(resultFormat)) {
            return `Measured in ${unit}`;
        }
        return `Measured in ${unit}, displays as ${formatChallengeResult(
            goal,
            resultFormat
        )}`;
    };

    const handleAddRule = () => {
        addGroupRule();
        validateRules();
    };

    const handleRemoveRule = (index: number) => {
        removeGroupRule(index);
        validateRules();
    };

    return (
        <RuleSetSection
            title="Group Rules"
            enabled={groupRules.value}
            disabled={disableGroupRulesEdit}
            toggleTestId="challenge-group-rules"
            onToggle={(checked) => {
                groupRules.onChange(checked);
                validateRules();
            }}
        >
            <div>
                <p className="text-lg font-semibold">
                    Collective Goals & Rewards
                </p>
                <p className="text-sm text-muted-foreground">
                    Create goals that the entire community works towards
                    together. When a goal is reached, all participants receive
                    the reward.
                </p>
            </div>

            {groupRulesList.map(
                (ruleForm: GroupRuleFormType, index: number) => (
                    <div
                        key={index}
                        className="flex flex-col gap-4 rounded-lg border bg-card p-4"
                    >
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-medium">
                                Goal #{index + 1}
                            </p>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive"
                                onClick={() => handleRemoveRule(index)}
                                disabled={disableGroupRulesEdit}
                                aria-label="Remove group goal"
                            >
                                <Trash2 />
                            </Button>
                        </div>
                        <TextField
                            type="number"
                            label="Goal"
                            value={ruleForm.$.activityPointsGoal.value || ''}
                            onChange={(value) => {
                                ruleForm.$.activityPointsGoal.onChange(
                                    Number(value)
                                );
                                validateRules();
                            }}
                            error={ruleForm.$.activityPointsGoal.error}
                            InputLabelShrink
                            disabled={disableGroupRulesEdit}
                            required
                            data-testid={`group-rule-${index}-points-goal`}
                        />
                        <Label>
                            {goalHint(ruleForm.$.activityPointsGoal.value)}
                        </Label>
                        <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
                            <TextField
                                type="number"
                                label="Reward amount"
                                value={ruleForm.$.reward.$.value.value || ''}
                                onChange={(value) => {
                                    ruleForm.$.reward.$.value.onChange(
                                        Number(value)
                                    );
                                    validateRules();
                                }}
                                error={ruleForm.$.reward.$.value.error}
                                InputLabelShrink
                                disabled={disableGroupRulesEdit}
                                data-testid={`group-rule-${index}-reward-amount`}
                            />
                            <CurrencySelect
                                value={ruleForm.$.reward.$.currencyCode.value}
                                onChange={(currencyCode) => {
                                    ruleForm.$.reward.$.currencyCode.onChange(
                                        currencyCode
                                    );
                                    validateRules();
                                }}
                            />
                        </div>
                    </div>
                )
            )}

            <Button
                variant="outline"
                className="w-full border-2 border-dashed py-6"
                onClick={handleAddRule}
                disabled={disableGroupRulesEdit}
                data-testid="add-group-rule-button"
            >
                <Plus />
                Add Group Goal
            </Button>

            {groupRulesList.length === 0 && (
                <div className="rounded-lg border-2 border-dashed bg-card p-6 text-center text-muted-foreground">
                    <p>No group goals defined yet.</p>
                    <p className="text-sm">
                        Click "Add Group Goal" to create collective challenges
                        for your community.
                    </p>
                </div>
            )}
        </RuleSetSection>
    );
});

export default GroupRules;
